/**
 * DASHBOARD-NPS
 * NPS score for the survey
 */

import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useEffect, useRef, useState } from "react"
import { FilterState } from "@/redux/filter.types"
import { Session } from "@/redux/_session.types"
import { store } from "@/index"
import { SurveyState } from "@/redux/survey.types"
import { fetchNps } from "@/redux/nps.actions"
import { fetchQuestions, loadQuestionsAndTopics } from "@/redux/_archive.actions"
import { Topic, TopicState } from "@/redux/topic.types"
import { AxisState } from "@/redux/axis.types"
import { Question, QuestionNps } from "@/redux/question.types"
import Card from "@/components/card"
import { flatten, sortBy } from "lodash"
import DashboardNumber from "@/components/dashboard-number"
import { faUser } from "@fortawesome/free-solid-svg-icons"
import DashboardRepartitionChartWidget from "./dashboard-repartition-chart.widget"
import Space from "@/components/space"
import DashboardNpsHeatmapWidget from "./dashboard-nps-heatmap.widget"
import DashboardNpsNoteWidget from "./dashboard-nps-note.widget"
import DashboardNpsBenchmarksWidget from "./dashboard-nps-benchmarks.widget"
import Modal from "@/components/modal"
import DashboardNpsSkeletonWidget from "./skeletons/dashboard-nps-skeleton.widget"
import { benchmarkFetchNps } from "@/redux/benchmark.actions"
import { useAppSelector } from "@/core/root-reducer"
import { selectCanSeeBenchmark } from "@/features/benchmark/entities/question/model/benchmarkQuestion.selector"
import {
  resolveNpsBenchmark,
  NpsBenchmarkEntry
} from "@/features/benchmark/entities/question/model/benchmarkQuestion.utils"

interface StateProps extends WithTranslation {
  _session: Session
  filter: FilterState
  survey: SurveyState
  topic: TopicState
  axis: AxisState
}

interface OwnProps {
  isOverview?: boolean
  isSmall?: boolean
  isReport?: boolean
}

type Props = StateProps & OwnProps

const MODAL_HELP = "MODAL_HELP"

function DashboardNpsWidget(props: Props) {
  const { t } = props
  const canSeeBenchmark = useAppSelector(selectCanSeeBenchmark)

  const [isLoading, setIsLoading] = useState(true)
  const [currentModal, setCurrentModal] = useState<null | string>(null)
  const [npsQuestions, setNpsQuestions] = useState<QuestionNps[]>([])

  //benchmarkFetchNps ne prend pas de paramètre par question (l'index eNPS est global au
  //compte, pas calculé par question) : un seul fetch ici, partagé par toutes les cards,
  //au lieu que chaque DashboardNpsBenchmarksWidget refasse le même appel indépendamment
  //(un compte avec beaucoup de questions taguées NPS multipliait sinon ce fetch par autant)
  const [npsBenchmarkEntry, setNpsBenchmarkEntry] = useState<NpsBenchmarkEntry | null>(null)
  const [isNpsBenchmarkLoading, setIsNpsBenchmarkLoading] = useState(true)
  const isBalancedWithoutBenchmark = !props.isSmall && !props.isOverview && !isNpsBenchmarkLoading && !npsBenchmarkEntry

  useEffect(() => {
    //isOverview ne rend jamais DashboardNpsBenchmarksWidget (cf plus bas) : pas la peine
    //de fetcher une donnée qui ne sera affichée nulle part. Idem sans accès au benchmark (observateur)
    if (props.isOverview || !canSeeBenchmark) {
      setIsNpsBenchmarkLoading(false)
      return
    }

    async function loadBenchmark() {
      setIsNpsBenchmarkLoading(true)
      const response: any = await store.dispatch(benchmarkFetchNps())
      setNpsBenchmarkEntry(response.error ? null : resolveNpsBenchmark(response))
      setIsNpsBenchmarkLoading(false)
    }

    loadBenchmark()
  }, [props.isOverview, canSeeBenchmark])

  //Même souci que le benchmark : DashboardRepartitionChartWidget refetchait toute la
  //liste des questions (fetchQuestions) pour n'en garder qu'une seule via .find(aid) —
  //une fois par question NPS affichée. Un seul fetch ici, partagé, la repartition de
  //chaque question est extraite et passée en props. null = pas encore fetché (l'enfant
  //reste en chargement) ; [] = fetché mais vide/en erreur (l'enfant arrête de charger,
  //comme l'ancien code qui appelait setIsLoading(false) même en cas d'erreur).
  const [questionsFullList, setQuestionsFullList] = useState<any[] | null>(null)

  useEffect(() => {
    //isSmall ne rend jamais DashboardRepartitionChartWidget (cf plus bas), et randomData
    //affiche la branche "unavailable" à la place des cards (cf plus bas) : dans les deux
    //cas, pas la peine de fetcher une donnée qui ne sera affichée nulle part
    if (props.isSmall || props.survey.active.randomData) {
      setQuestionsFullList([])
      return
    }

    async function loadQuestionsFullList() {
      const response: any = await store.dispatch(
        fetchQuestions(props.filter.dashboard, null, undefined, undefined, props.isReport)
      )
      setQuestionsFullList(response.error ? [] : response)
    }

    loadQuestionsFullList()
  }, [props.filter.dashboard, props.isReport, props.isSmall, props.survey.active.randomData])

  //Chaque card NPS contient 2 sous-composants avec leur propre chargement indépendant
  //(barres de répartition + heatmap par service). Sans coordination, on aurait la même
  //cascade que sur /dashboard/messages : le skeleton principal se lève, puis chaque
  //sous-composant affiche brièvement le sien. On les garde donc montés (pour qu'ils
  //démarrent leur fetch) mais cachés tant que tous n'ont pas fini au moins une fois.
  const [childStatuses, setChildStatuses] = useState<Record<string, boolean>>({})
  const hasLoadedOnceRef = useRef(false)

  function reportChildStatus(key: string, loading: boolean) {
    setChildStatuses((prev) => (prev[key] === loading ? prev : { ...prev, [key]: loading }))
  }

  //Si randomData, la branche "unavailable" s'affiche à la place des cards — leurs
  //sous-composants ne montent jamais, donc aucune clé ne doit être attendue ici (sinon
  //isSkeletonVisible resterait bloqué à true indéfiniment, cf revue de session)
  const expectedChildKeys = props.survey.active.randomData
    ? []
    : flatten(npsQuestions.map((q) => (props.isSmall ? [`${q.aid}-heatmap`] : [`${q.aid}-bars`, `${q.aid}-heatmap`])))
  const allChildrenReady = expectedChildKeys.every((key) => childStatuses[key] === false)

  useEffect(() => {
    if (!isLoading && allChildrenReady) {
      hasLoadedOnceRef.current = true
    }
  })

  const isSkeletonVisible = isLoading || (!hasLoadedOnceRef.current && !allChildrenReady)

  //Load count of message every times filters are changed
  //All level should be greater than 2
  useEffect(() => {
    async function load() {
      setIsLoading(true)

      if (props._session.modules.survey.nps && props.survey.active.id.length > 0) {
        const response: any = await store.dispatch(fetchNps(props.filter.dashboard, props.isReport))

        if (response.error || response.length < 1) {
          setNpsQuestions([])
        } else {
          setNpsQuestions(sortBy(response, "nps").reverse())
        }
      }

      setIsLoading(false)
    }

    load()
  }, [
    props.survey.active.id,
    props._session.modules.survey.nps,
    props.survey.active.randomData,
    props.filter.dashboard
  ])

  // topic.list's per-question `.note` (used below by getQuestion) is only populated by
  // loadQuestionsAndTopics, normally triggered by dashboard-questions.widget.tsx. Dispatch it
  // here too — /dashboard/nps can be the first page loaded in a session, with that widget
  // never mounting, leaving topic.list's questions with note=null (score shows as 0).
  useEffect(() => {
    store.dispatch(loadQuestionsAndTopics(props.filter.dashboard))
  }, [props.filter.dashboard, props.survey.active.randomData])

  function getQuestion(questionAid) {
    const questions = flatten(props.topic.list.map((x) => x.Questions))
    const question = questions.find((x) => x.aid === questionAid)
    return new Question(question)
  }

  function getTopic(questionAid) {
    const topic = props.topic.list.find((x) => x.Questions.map((q) => q.aid).indexOf(questionAid) > -1)
    return new Topic(topic)
  }

  function getAxisColor(questionAid) {
    const axis = props.axis.list.find((a) => a.id === getTopic(questionAid).AxisId)
    return axis?.color || "#9CA3AF"
  }

  return (
    <div className="rel">
      {currentModal === MODAL_HELP && (
        <Modal onClose={() => setCurrentModal(null)} isCloseButtonVisible title={t("nps_help_1")}>
          <p>{t("nps_help_2")}</p>

          <ul>
            <li>{t("nps_help_detractors")}</li>
            <li>{t("nps_help_neutral")}</li>
            <li>{t("nps_help_promotors")}</li>
          </ul>
        </Modal>
      )}

      {/* Toujours monté dès que npsQuestions est là (pas gated par isSkeletonVisible) pour que
      les sous-composants démarrent leur propre chargement pendant qu'on affiche encore le
      skeleton par-dessus — juste caché visuellement tant que ce n'est pas prêt. */}
      <div style={{ display: isSkeletonVisible ? "none" : undefined }}>
        {props.survey.active.randomData ? (
          <p className="grey-t">
            <b>{t("utils_unavailable")}</b>
          </p>
        ) : (
          <div>
            {!props.isOverview && (
              <b className="grey-t">
                {t("nps_explanation")}{" "}
                <span className="_hover" onClick={() => setCurrentModal(MODAL_HELP)}>
                  <u>{t("nps_help_1")}</u>
                </span>
              </b>
            )}
            {!props.isOverview && <div className="height-20" />}

            {!isLoading && !npsQuestions.length && (
              <p className="grey-t">
                <b>{t("nps_empty")}</b>
              </p>
            )}

            {npsQuestions.map((question: QuestionNps) => (
            <div key={question.aid} className="flex" style={{ width: 1000 }}>
              <Card className="flex1" exportName={getQuestion(question.aid).label} helpSection="regle">
                <div
                  style={{
                    width: props.isSmall ? 300 : undefined,
                    marginTop: props.isSmall ? -40 : undefined
                  }}>
                  <p>
                    <b>{getTopic(question.aid).label}</b>
                  </p>
                  <p>{getQuestion(question.aid).label}</p>
                </div>

                <div className="height-20" />

                <div
                  className={isBalancedWithoutBenchmark ? "grid" : "flex"}
                  style={isBalancedWithoutBenchmark ? { gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)", alignItems: "center" } : undefined}>
                  <div className={`flex flex-dcol${isBalancedWithoutBenchmark ? " items-center" : ""}`} style={{ minHeight: 230 }}>
                    <Space />
                    <DashboardNpsNoteWidget npsNote={question.nps} fontSize={48} />
                    {!props.isOverview && !isBalancedWithoutBenchmark && (
                      <div className="mt-2">
                        <DashboardNpsBenchmarksWidget
                          npsScore={question.nps}
                          questionLabel={getQuestion(question.aid).label}
                          topicLabel={getTopic(question.aid).label}
                          axisColor={getAxisColor(question.aid)}
                          npsEntry={npsBenchmarkEntry}
                          isLoading={isNpsBenchmarkLoading}
                        />
                      </div>
                    )}
                    <Space />
                  </div>

                  {!isBalancedWithoutBenchmark && <Space />}

                  {!props.isSmall && (
                    <DashboardRepartitionChartWidget
                      questionNpsAid={question.aid}
                      isReport={props.isReport}
                      repartitionValues={
                        questionsFullList
                          ? questionsFullList.find((x) => x.aid === question.aid)?.repartition ?? []
                          : undefined
                      }
                      onLoadingChange={(loading) => reportChildStatus(`${question.aid}-bars`, loading)}
                    />
                  )}

                  {!isBalancedWithoutBenchmark && <Space />}

                  {!props.isSmall && (
                    <div className={`flex flex-dcol${isBalancedWithoutBenchmark ? " items-center" : ""}`} style={isBalancedWithoutBenchmark ? { minHeight: 230 } : undefined}>
                      <Space />

                      {!props.isOverview && (
                        <DashboardNumber
                          isLoading={isLoading}
                          count={question.total}
                          icon={faUser}
                          header={t("nps_users_header")}
                          title={t("nps_users")}
                        />
                      )}

                      <Space />
                    </div>
                  )}

                  {!isBalancedWithoutBenchmark && <Space />}
                </div>

                <DashboardNpsHeatmapWidget
                  questionAid={question.aid}
                  isReport={props.isReport}
                  onLoadingChange={(loading) => reportChildStatus(`${question.aid}-heatmap`, loading)}
                />
              </Card>
            </div>
            ))}
          </div>
        )}
      </div>

      {isSkeletonVisible && (
        <div style={{ width: 1000, margin: "0 auto" }}>
          <Card className="flex1" isWithoutPadding isFullWidth>
            <DashboardNpsSkeletonWidget />
          </Card>
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  filter: state.filter,
  survey: state.survey,
  topic: state.topic,
  axis: state.axis
})

export default connect(mapStateToProps)(withTranslation()(DashboardNpsWidget))
